import {
  ComponentFactory,
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { IconSizeET } from '@liberty-financial/ng-components/lib/components/icon/icon.enum';
import { Observable, Subscription, timer } from 'rxjs';

import { Failure } from '@core/failure';
import { MonitoringService } from '@core/services/monitoring.service';

import { reportError } from './error-classification';
import { FailureComponent } from './failure/failure.component';
import { LoadingIndicatorComponent } from './loading-indicator.component';

type LoadingState = 'Loading' | 'Loaded' | 'Failed';

type LoadedValue<T> = {
  $implicit: T;
};

// The load directive subscribes to the provided Observable<T>, and presents a loading indicator until a value is
// produced by the observable. This can be used with any Observable<T>, regardless of origin or context, but the
// primary intended purpose of this load directive is to encapsulate the concerns of loading content from a HTTP
// request, including waiting for the response, presenting a loading indication to the user, and handling errors. The
// load directive will guard the template, presenting it only when the observable has produced a value, and it also
// supplies the produced value, of type T, to the template. If the observable produces an error instead of a value, the
// load directive will present an appropriate message.
//
// To use the load directive in a component, first declare an Observable<T> as a property of the component, which
// defines the necessary operation for loading the content. For example:
//
//   name$: Observable<string> = of('John').pipe(delay(1000))
//
// It may be necessary to use observable operators (such as map, flatMap, and others) to combine and alter the loading
// operation, to convert it to an observable of the desired type. For example, if the content is to be loaded through a
// service which performs a HTTP request, but the response needs to be converted to a model type, something similar to
// the following may be required:
//
//   user$: Observable<User> = this.userService.getUser(userId).pipe(map(payload => this.convertToModel(payload)))
//
// Other scenarios may require even more complex observable definitions.
//
// Once the observable has been appropriately defined, the next step is to apply the load directive on an ng-container
// in the template. For example:
//
//   <ng-container *appLoad="let name of name$">
//   </ng-container>
//
// Within the ng-container with the load directive, the loaded value can now be used. For example:
//
//   <ng-container *appLoad="let name of name$">
//     <p>Hello, {{name}}!</p>
//   </ng-container>
//
// A default value can be provided, which will be used if the observable produces an error, instead of a value. For
// example:
//
//   <ng-container *appLoad="let result of calculation$; default: 42"></ng-container>
//
// A loading template can be supplied, to be displayed while waiting for the observable to produce a result. If no
// loading template is supplied, the standard loading indicator will be presented. The following example demonstrates
// how to supply a custom loading template:
//
//   <ng-container *appLoad="let value of value$; loading: pleaseWait"></ng-container>
//   <ng-template #pleaseWait><p>Please wait...</p></ng-template>
//
// An error template can be supplied, to be presented when the observable produces an error, instead of a value. If no
// error template is supplied, the load directive will present a standard error template, containing an appropriate
// explanation to the user. If a default value has been provided (see above), the error template will be ignored. The
// following example demonstrates how to supply a custom error template:
//
//   <ng-container *appLoad="let value of value$; error: error"></ng-container>
//   <ng-template #error><p>Ooops!</p></ng-template>
//
// Note: To test the error template in development, requires introducing a delay before throwing an error on the value$.
// i.e. value$: Observable<any> = concat(of({}).pipe(delay(5000)), throwError(new Error()));
//
// The size of the standard error template can be configured by specifying the errorSizeClass property. By default, a
// full-size error template will be presented. This can therefore be useful in situations where the content area for the
// loaded data is small. If a custom error template has been supplied (see above), the errorSizeClass property will be
// ignored. The following example demonstrates how to specify a small version of the standard error template:
//
//   <ng-container *appLoad="let value of value$; errorSizeClass: 'sm'"></ng-container>
/**
 * @deprecated().  Please use [sparkLoad] in Core module
 */
@Directive({
  selector: '[appLoad]'
})
export class LoadDirective<T, E> implements OnInit, OnDestroy {
  private state: LoadingState = 'Loading';
  private loadingIndicatorFactory: ComponentFactory<LoadingIndicatorComponent>;
  private failureComponentFactory: ComponentFactory<FailureComponent>;

  static ngTemplateContextGuard<T, E>(
    dir: LoadDirective<T, E>,
    ctx: LoadedValue<T>
  ): ctx is LoadedValue<T> {
    return true;
  }

  private subscriptions: Subscription[] = [];

  @Input('appLoadOf') set observable(o: Observable<T | Failure<E>>) {
    this.subscriptions.push(
      o.subscribe(
        (value: T | Failure<E>) => {
          if (value instanceof Failure) {
            this.failure(value.error);
          } else {
            this.loaded(value);
          }
        },
        (error) => this.failure(error)
      )
    );
  }

  @Input('appLoadSize') size: 'sm' | 'md' | 'lg' = 'lg';

  @Input('appLoadPosition') position: 'absolute' | 'relative' = 'absolute';

  @Input('appLoadDefault') defaultValue: T | null = null;

  @Input('appLoadLoading') loadingTemplate: TemplateRef<void> | null = null;

  @Input('appLoadError') errorTemplate: TemplateRef<any> | null = null;

  @Input('appLoadErrorSizeClass') errorSizeClass: IconSizeET = 'lg';

  @Input('appLoadLoadingMessage') loadingMessage: string = '';

  @Input('appLoadTrackingName') trackingName: string | null = null;

  constructor(
    private templateRef: TemplateRef<LoadedValue<T>>,
    private viewContainer: ViewContainerRef,
    private monitoringService: MonitoringService,
    componentFactoryResolver: ComponentFactoryResolver
  ) {
    this.loadingIndicatorFactory =
      componentFactoryResolver.resolveComponentFactory(
        LoadingIndicatorComponent
      );
    this.failureComponentFactory =
      componentFactoryResolver.resolveComponentFactory(FailureComponent);
  }

  timer$: Observable<number> = timer(3000);

  ngOnInit(): void {
    this.loading();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private loading(): void {
    if (this.state == 'Loading') {
      this.viewContainer.clear();
      if (this.loadingTemplate != null) {
        this.viewContainer.createEmbeddedView(this.loadingTemplate);
      } else {
        const loadingIndicator = this.viewContainer.createComponent(
          this.loadingIndicatorFactory
        );
        loadingIndicator.instance.size = this.size;
        loadingIndicator.instance.position = this.position;

        if (this.loadingMessage != '') {
          this.subscriptions.push(
            this.timer$.subscribe((x) => {
              loadingIndicator.instance.message = this.loadingMessage;
            })
          );
        }
      }
    }
  }

  private loaded(value: T): void {
    this.viewContainer.clear();
    this.state = 'Loaded';
    this.viewContainer.createEmbeddedView(this.templateRef, {
      $implicit: value
    });
  }

  private failure(error: any): void {
    this.viewContainer.clear();
    
    if (this.defaultValue != null) {
      this.state = 'Loaded';
      this.viewContainer.createEmbeddedView(this.templateRef, {
        $implicit: this.defaultValue
      });
    } else {
      this.state = 'Failed';

      if (this.errorTemplate != null) {
        this.viewContainer.createEmbeddedView(this.errorTemplate);
      } else {
        reportError(error);
        const failureComponent = this.viewContainer.createComponent(
          this.failureComponentFactory
        );
        failureComponent.instance.error = error;
        failureComponent.instance.sizeClass = this.errorSizeClass;
      }
    }
    
    reportError(error);
  }
}
